/**
 * When `path` is `dir` itself or nested under it, return the remainder with the
 * leading slash stripped (`''` when `path` *is* the directory). Returns `null`
 * when `path` isn't under `dir` at all.
 *
 * Trailing slashes on `dir` are normalized away first: a working directory can
 * arrive either way, and under `/home/user/reflex/` the path
 * `/home/user/reflex` is that same directory, not somewhere outside it.
 */
function stripPrefix(path: string, dir: string): string | null {
  const base = dir.replace(/\/+$/, '');
  // The filesystem root prefixes every absolute path, so trimming it would
  // turn each one into a bare relative path that no longer reads as absolute.
  if (base === '') return null;
  if (path === base) return '';
  if (path.startsWith(`${base}/`)) return path.slice(base.length + 1);
  return null;
}

/**
 * Render an absolute tool-call path the way a person working in that directory
 * would type it: `src/x.ts` rather than `/home/user/reflex/src/x.ts`. The
 * working directory is the same on every row of a session, so repeating it on
 * each path is noise that pushes the informative tail out of a narrow row.
 *
 * Only paths *inside* the working directory are shortened. Everything else is
 * returned untouched — including when the session announced no directory at
 * all (ACP/OpenCode announces none). A path outside the working tree is exactly
 * the case where the prefix carries information, and there is no honest way to
 * abbreviate it: the agent's home directory is not something the transcript
 * records, and this code runs in a browser with no view of the devbox, so a `~`
 * would be a guess about a filesystem it cannot read.
 */
export function relativeToWorkingDir(path: string, workingDir: string | null): string {
  if (!path.startsWith('/') || !workingDir?.startsWith('/')) return path;
  const relative = stripPrefix(path, workingDir);
  if (relative === null) return path;
  // The directory itself has no remainder; `.` is what a shell calls it.
  return relative || '.';
}
